/**
 * ResetPassword Page
 *
 * Set new password after clicking reset link from email
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, CheckCircle, Lock, AlertCircle } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { validatePassword, supabase } from '../../lib/supabase';
import PasswordInput from '../../components/auth/PasswordInput';

export function ResetPassword() {
  const navigate = useNavigate();
  const { updatePassword, user } = useAuth();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [hasSession, setHasSession] = useState(false);
  const [initializing, setInitializing] = useState(true);

  // Handle password reset link - parse URL params and establish session
  useEffect(() => {
    const handleResetLink = async () => {
      try {
        // Check for PKCE flow (code in query string) - this is the newer Supabase flow
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');

        // Also check hash params for older flow
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        const type = hashParams.get('type');

        console.log('[ResetPassword] URL params:', { hasCode: !!code, hasAccessToken: !!accessToken, type });

        // PKCE flow: exchange code for session
        if (code) {
          console.log('[ResetPassword] Exchanging code for session (PKCE flow)');
          const { data, error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);

          if (exchangeError) {
            console.error('[ResetPassword] Failed to exchange code:', exchangeError);
            setInitializing(false);
            return;
          }

          console.log('[ResetPassword] Session established successfully');
          // Clear the code from URL for cleaner look
          window.history.replaceState(null, '', window.location.pathname);
          setHasSession(true);
        }
        // Legacy hash-based flow
        else if (accessToken && refreshToken && type === 'recovery') {
          console.log('[ResetPassword] Setting session from recovery tokens (legacy flow)');
          const { error: setSessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          });

          if (setSessionError) {
            console.error('[ResetPassword] Failed to set session:', setSessionError);
            setInitializing(false);
            return;
          }

          // Clear the hash from URL for cleaner look
          window.history.replaceState(null, '', window.location.pathname);
          setHasSession(true);
        } else if (user) {
          // Already have a user session
          setHasSession(true);
        }
      } catch (err) {
        console.error('[ResetPassword] Error handling reset link:', err);
      } finally {
        setInitializing(false);
      }
    };

    handleResetLink();
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validate password
    const { isValid } = validatePassword(password);
    if (!isValid) {
      setError('Please meet all password requirements.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsLoading(true);

    try {
      const { error: updateError } = await updatePassword(password);

      if (updateError) {
        setError(updateError.message);
      } else {
        setSuccess(true);
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      }
    } catch (err) {
      setError('Failed to update password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading while initializing
  if (initializing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Verifying reset link...</p>
        </div>
      </div>
    );
  }

  // If no session after initialization, show error
  if (!hasSession && !success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col">
        <header className="py-6 px-4">
          <div className="max-w-md mx-auto">
            <Link to="/login" className="flex items-center gap-2 text-blue-600 hover:text-blue-700">
              <ArrowLeft className="w-5 h-5" />
              Back to login
            </Link>
          </div>
        </header>

        <main className="flex-1 flex items-center justify-center px-4 py-8">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Invalid or Expired Link
            </h1>
            <p className="text-gray-600 mb-6">
              This password reset link is invalid or has expired.
              Please request a new one.
            </p>
            <Link
              to="/auth/forgot-password"
              className="w-full py-3 px-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors inline-block"
            >
              Request New Link
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col">
      {/* Header */}
      <header className="py-6 px-4">
        <div className="max-w-md mx-auto">
          <Link to="/login" className="flex items-center gap-2 text-blue-600 hover:text-blue-700">
            <ArrowLeft className="w-5 h-5" />
            Back to login
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8">
          {success ? (
            // Success State
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Password Updated!
              </h1>
              <p className="text-gray-600 mb-6">
                Your password has been successfully updated.
                You'll be redirected to login shortly.
              </p>

              <Link
                to="/login"
                className="w-full py-3 px-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors inline-block"
              >
                Go to Login
              </Link>
            </div>
          ) : (
            // Form State
            <>
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lock className="w-8 h-8 text-blue-600" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  Create new password
                </h1>
                <p className="text-gray-600">
                  Enter your new password below.
                </p>
              </div>

              <form onSubmit={handleSubmit}>
                <PasswordInput
                  value={password}
                  onChange={setPassword}
                  label="New Password"
                  showRequirements={true}
                  className="mb-4"
                />

                <PasswordInput
                  value={confirmPassword}
                  onChange={setConfirmPassword}
                  label="Confirm New Password"
                  placeholder="Confirm your new password"
                  className="mb-6"
                />

                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg mb-4 text-red-700 text-sm">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isLoading || !password || !confirmPassword}
                  className="w-full py-3 px-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    'Update Password'
                  )}
                </button>
              </form>
            </>
          )}
        </div>
      </main>
    </div>
  );
}

export default ResetPassword;
